import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { useEffect, useState, useRef, useMemo } from "react";
import { search } from "../../api/ProjectApi";
import { useSelector, useDispatch } from "react-redux";
import { fromJS } from "immutable";
import { renderItemIcon } from "./LinkItemPickerV2";
import { XCircleIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { ContentTypes } from "../../constants/action-types";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/shadcn/popover";
import { cn } from "../../lib/utils";
import { formatAmount } from "../../api/DataTypeUtils";
import DefaultAvatar from "../../components/DefaultAvatar";
import { BadgeDollarSign, User, Building, Grid3x3 } from "lucide-react";
import { showCreateItemDialog } from "../../Navigator";
import useDebounce from "../../useDebounce";

// Helper functions (pure functions, no dependencies on component state)
const highlightText = (text: string, query: string) => {
  if (!text || !query || query.trim() === "") {
    return text;
  }

  const textStr = String(text);
  const queryStr = query.trim();
  const escapedQuery = queryStr.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escapedQuery})`, "gi");
  const parts = textStr.split(regex);

  return parts.map((part, index) => {
    if (part.toLowerCase() === queryStr.toLowerCase()) {
      return (
        <mark key={index} className="bg-yellow-200 text-gray-900 font-medium">
          {part}
        </mark>
      );
    }
    return part;
  });
};

const getLabelFromValue = (value: any): string => {
  if (!value) return "";
  if (typeof value === "object" && value.get) {
    return value.get("label") || "";
  }
  return value?.label || String(value);
};

interface SearchBoxProps {
  onSelectItem: (item: any) => void;
  fullWidth?: boolean;
  placeholder?: React.ReactNode;
  isBorder?: boolean;
  collapsible?: boolean;
}

export default function SearchBox({
  onSelectItem,
  fullWidth = false,
  placeholder = undefined,
  isBorder = false,
  collapsible = false,
}: SearchBoxProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  // Redux selectors
  const project = useSelector((state: any) => state.projectReducer.get("project"));
  const projectCurrency = project?.get?.("currency") || "USD";
  const buckets = useSelector((state: any) => state.bucketReducer.get("SALE")) || fromJS([]);

  // State
  const [rawQuery, setRawQuery] = useState<string>("");
  const [showSearchResult, setShowSearchResult] = useState(false);
  const [type, setType] = useState<string | undefined>(undefined);
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(undefined);
  const [searchResult, setSearchResult] = useState({
    pending: false,
    pressSearch: false,
    pageResult: fromJS({ content: [], totalElements: 0 }),
  });

  // Refs
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Derived values
  const { pending, pageResult, pressSearch } = searchResult;
  const items = pageResult.get("content");
  const totalElements = pageResult.get("totalElements");
  const hasKeywords = useMemo(() => rawQuery && rawQuery.trim() !== "", [rawQuery]);

  useEffect(() => {
    if (showSearchResult && !type) {
      setType("ALL");
    }
  }, [showSearchResult, type]);

  useEffect(() => {
    if (!showSearchResult) return;

    const rafId = requestAnimationFrame(() => {
      if (triggerRef.current) {
        const width = triggerRef.current.offsetWidth;
        if (width) setTriggerWidth(width);
      } else if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        if (width) setTriggerWidth(width);
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [showSearchResult]);

  // Search logic
  const getSearchTypes = (): string[] => {
    if (!type || type === "ALL") {
      return [ContentTypes.SALE, "CONTACT", "COMPANY"];
    }
    return [type];
  };

  const doSearch = () => {
    const filters = hasKeywords
      ? {
          name: {
            operator: "regex",
            values: [rawQuery],
          },
        }
      : {};

    setSearchResult((prev) => ({
      ...prev,
      pending: true,
      pressSearch: true,
    }));

    search({
      projectId: project.get("id"),
      pageSize: 5,
      types: getSearchTypes(),
      filters,
    }).then((result) => {
      setSearchResult((prev) => ({
        ...prev,
        pending: false,
        pressSearch: true,
        pageResult: fromJS(result.pageResult),
      }));
    });
  };

  useDebounce(
    () => {
      if (showSearchResult && type) {
        doSearch();
      }
    },
    [rawQuery, type, showSearchResult],
    500
  );

  // Handlers
  const handleSearch = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      doSearch();
    }
  };

  const handleCreateItem = (itemType: string) => {
    const itemName = rawQuery.trim();
    if (!itemName) return;

    const itemData = {
      projectId: project.get("id"),
      type: itemType,
      data: fromJS({
        name: { value: itemName },
      }),
    };

    showCreateItemDialog({
      dispatch,
      type: itemType,
      item: fromJS(itemData),
      useInApp: true,
      onClose: () => {
        if (type) doSearch();
      },
    });

    setShowSearchResult(false);
  };

  // Extract additional info helpers
  const getDealAdditionalInfo = (itemData: any): string[] => {
    const info: string[] = [];

    const estimatedRevenue = itemData.getIn(["estimatedRevenue", "value"]);
    if (estimatedRevenue) {
      const revenueStr = formatAmount(i18n.language, estimatedRevenue, projectCurrency, "");
      if (revenueStr) info.push(revenueStr);
    }

    const contact = itemData.getIn(["contact", "value"]);
    const contactLabel = getLabelFromValue(contact);
    if (contactLabel) info.push(contactLabel);

    const account = itemData.getIn(["account", "value"]);
    const accountLabel = getLabelFromValue(account);
    if (accountLabel) info.push(accountLabel);

    const bucket = itemData.getIn(["bucketId", "value"]);
    if (bucket) {
      const bucketItem = buckets.find((b: any) => String(b.get("id")) === String(bucket));
      if (bucketItem) {
        const bucketName = bucketItem.get("name");
        if (bucketName) info.push(String(bucketName));
      }
    }

    return info;
  };

  const getContactAdditionalInfo = (itemData: any): string[] => {
    const info: string[] = [];

    const account = itemData.getIn(["account", "value"]);
    const accountLabel = getLabelFromValue(account);
    if (accountLabel) info.push(accountLabel);

    const email = itemData.getIn(["email", "value"]);
    if (email) info.push(String(email));

    return info;
  };

  const getCompanyAdditionalInfo = (itemData: any): string[] => {
    const info: string[] = [];

    const website = itemData.getIn(["website", "value"]);
    if (website) info.push(String(website));

    const industry = itemData.getIn(["industry", "value"]);
    const industryLabel = getLabelFromValue(industry);
    if (industryLabel) info.push(industryLabel);

    // Address information
    const addressParts: string[] = [];
    const street = itemData.getIn(["street", "value"]);
    if (street) addressParts.push(String(street));

    const city = itemData.getIn(["city", "value"]);
    const cityLabel = getLabelFromValue(city);

    const country = itemData.getIn(["country", "value"]);
    const countryLabel = getLabelFromValue(country);

    if (cityLabel && countryLabel) {
      addressParts.push(`${cityLabel}, ${countryLabel}`);
    } else if (cityLabel) {
      addressParts.push(cityLabel);
    } else if (countryLabel) {
      addressParts.push(countryLabel);
    }

    if (addressParts.length > 0) {
      info.push(addressParts.join(", "));
    }

    return info;
  };

  const getItemAdditionalInfo = (itemType: string, itemData: any): string[] => {
    if (itemType === "SALE" || itemType === ContentTypes.SALE) {
      return getDealAdditionalInfo(itemData);
    }
    if (itemType === "CONTACT" || itemType === ContentTypes.CONTACT) {
      return getContactAdditionalInfo(itemData);
    }
    if (itemType === "COMPANY" || itemType === ContentTypes.COMPANY) {
      return getCompanyAdditionalInfo(itemData);
    }
    return [];
  };

  // Render helpers
  const getSearchResultLabel = () => {
    return hasKeywords ? t("SEARCH RESULTS") : t("RECENTLY ADDED");
  };

  const renderItemAvatar = (
    itemType: string,
    iconUrl: string | undefined,
    itemName: string
  ): React.ReactNode => {
    if (iconUrl) {
      const isCompany = itemType === "COMPANY" || itemType === ContentTypes.COMPANY;
      return (
        <img
          src={iconUrl}
          alt=""
          className={cn("rounded-full object-cover shrink-0 w-8 h-8", isCompany && "rounded-2xl")}
        />
      );
    }

    const avatarType =
      itemType === "COMPANY" || itemType === ContentTypes.COMPANY ? "COMPANY" : "CONTACT";
    return <DefaultAvatar name={itemName || ""} type={avatarType} size="sm" className="shrink-0" />;
  };

  const renderItemDisplay = (itemType: string, iconUrl: string | undefined, itemName: string) => {
    const isContactOrAccount =
      itemType === "CONTACT" ||
      itemType === ContentTypes.CONTACT ||
      itemType === "COMPANY" ||
      itemType === ContentTypes.COMPANY;

    if (isContactOrAccount) {
      return renderItemAvatar(itemType, iconUrl, itemName);
    }

    return <div className="shrink-0">{renderItemIcon(itemType)}</div>;
  };

  const renderSearchResultSkeleton = () => (
    <div className="space-y-1">
      <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
        {t("SEARCH RESULTS")}
      </div>
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={`skeleton-${index}`} className="flex items-center space-x-3 px-4 py-2">
          <div className="relative h-8 w-8 shrink-0 overflow-hidden rounded-full bg-gray-200">
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/70 to-transparent" />
          </div>
          <div className="flex-1 min-w-0 space-y-1.5">
            <div className="relative h-4 w-3/4 overflow-hidden rounded bg-gray-200">
              <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/70 to-transparent" />
            </div>
            <div className="relative h-3 w-1/2 overflow-hidden rounded bg-gray-200">
              <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/70 to-transparent [animation-delay:0.2s]" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderNoResults = () => {
    const queryText = rawQuery.trim();
    const hasQuery = queryText !== "";

    return (
      <div className="flex flex-col items-center justify-center py-8 px-4">
        <div className="relative mb-4">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
            <MagnifyingGlassIcon className="w-8 h-8 text-gray-400" />
          </div>
          <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
            <PlusIcon className="w-4 h-4 text-blue-600" />
          </div>
        </div>

        <p className="text-sm font-semibold text-gray-900 mb-6 text-center">
          {hasQuery ? (
            <>
              {t("No results for")} <span className="text-gray-600">'{queryText}'</span>
            </>
          ) : (
            t("No results")
          )}
        </p>

        {hasQuery && (
          <div className="w-full space-y-2">
            {[
              { type: ContentTypes.SALE, icon: BadgeDollarSign, label: t("Add deal") },
              { type: ContentTypes.CONTACT, icon: User, label: t("Add contact") },
              { type: ContentTypes.COMPANY, icon: Building, label: t("Add account") },
            ].map(({ type: itemType, icon: Icon, label }) => (
              <button
                key={itemType}
                onClick={() => handleCreateItem(itemType)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span>
                  {label} <span className="font-medium">'{queryText}'</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderSearchResult = () => {
    if (pending) return renderSearchResultSkeleton();

    if (totalElements === 0 && pressSearch && hasKeywords) {
      return renderNoResults();
    }

    if (totalElements > 0) {
      return (
        <div className="space-y-1">
          <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
            {getSearchResultLabel()}
          </div>
          {items.map((item: any) => {
            const itemData = item.get("data");
            const itemName = itemData.getIn(["name", "value"]);
            const itemType = item.get("type");
            const iconUrl = item.get("iconUrl");
            const additionalInfo = getItemAdditionalInfo(itemType, itemData);

            return (
              <div
                key={item.get("id")}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelectItem(item);
                  setShowSearchResult(false);
                }}
                className="flex items-center space-x-3 px-4 py-2 hover:bg-gray-50 cursor-pointer"
              >
                {renderItemDisplay(itemType, iconUrl, itemName)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {highlightText(itemName, rawQuery)}
                  </p>
                  {additionalInfo.length > 0 ? (
                    <p className="text-xs text-gray-500 truncate">{additionalInfo.join(" · ")}</p>
                  ) : (
                    <p className="text-xs text-gray-500 capitalize">{itemType.toLowerCase()}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return null;
  };

  const renderTypeActions = () => {
    const tabs = [
      { key: "ALL", label: t("All"), value: !type || type === "ALL", icon: Grid3x3 },
      {
        key: ContentTypes.SALE,
        label: t("Deal"),
        value: type === ContentTypes.SALE,
        icon: BadgeDollarSign,
      },
      { key: "CONTACT", label: t("Contact"), value: type === "CONTACT", icon: User },
      { key: "COMPANY", label: t("Account"), value: type === "COMPANY", icon: Building },
    ];

    return (
      <div className="flex items-center gap-1.5 w-full px-3 py-2.5 border-b border-gray-100">
        {tabs.map((tab) => {
          const isActive = tab.value;
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setType(tab.key)}
              className={cn(
                "inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-200",
                "border border-gray-200",
                isActive
                  ? "bg-blue-50 text-blue-700 ring-blue-200 shadow-sm"
                  : "bg-gray-50 text-gray-600 ring-gray-200 hover:bg-gray-100 hover:text-gray-700"
              )}
            >
              <Icon className={cn("w-3.5 h-3.5", isActive ? "text-blue-600" : "text-gray-500")} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const handleCollapsibleIconClick = () => {
    setIsExpanded(true);
    setShowSearchResult(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleCollapsibleBlur = () => {
    if (!rawQuery) {
      setIsExpanded(false);
    }
  };

  const renderActivator = () => {
    if (placeholder) return placeholder;

    // Collapsible mode: icon-only → expand on click
    if (collapsible && !isExpanded) {
      return (
        <button
          type="button"
          onClick={handleCollapsibleIconClick}
          className="flex items-center justify-center w-8 h-8 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
          aria-label={t("Search...")}
        >
          <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      );
    }

    return (
      <div
        className={cn(
          "relative",
          collapsible
            ? "flex items-center w-105 animate-in fade-in slide-in-from-right-2 duration-150"
            : isBorder
              ? "flex flex-col items-start p-0 w-full h-[30px]"
              : "flex grow items-stretch focus-within:z-10",
          fullWidth && !collapsible && "w-full"
        )}
      >
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </div>
        <input
          ref={inputRef}
          onChange={(e) => setRawQuery(e.target.value)}
          onKeyDown={handleSearch}
          onBlur={collapsible ? handleCollapsibleBlur : undefined}
          className={cn(
            "w-full block rounded-md pl-10 placeholder:text-gray-400",
            collapsible
              ? "h-8 border border-gray-300 focus:border-blue-400 focus:outline-none text-sm"
              : isBorder
                ? "h-full"
                : "py-1",
            !collapsible &&
              (isBorder
                ? "border border-gray-300 focus:border-blue-300"
                : "border-0 ring-inset ring-gray-300")
          )}
          placeholder={t("Search...")}
        />
        {collapsible && rawQuery && (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              setRawQuery("");
              setIsExpanded(false);
              setShowSearchResult(false);
              if (inputRef.current) (inputRef.current as HTMLInputElement).value = "";
            }}
            className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600"
          >
            <XCircleIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  const handlePointerDownOutside = (e: any) => {
    const target = e.target as HTMLElement;
    if (
      popoverContentRef.current?.contains(target) ||
      inputRef.current === target ||
      triggerRef.current?.contains(target)
    ) {
      e.preventDefault();
    }
  };

  const handleFocusOutside = (e: any) => {
    const target = e.target as HTMLElement;
    if (
      popoverContentRef.current?.contains(target) ||
      inputRef.current === target ||
      triggerRef.current?.contains(target)
    ) {
      e.preventDefault();
    }
  };

  const handleInteractOutside = (e: any) => {
    const target = e.target as HTMLElement;
    if (
      popoverContentRef.current?.contains(target) ||
      inputRef.current === target ||
      triggerRef.current?.contains(target)
    ) {
      e.preventDefault();
    }
  };

  const handleOpenAutoFocus = (e: Event) => {
    e.preventDefault();

    if (!triggerWidth && triggerRef.current) {
      const width = triggerRef.current.offsetWidth;
      if (width) setTriggerWidth(width);
    } else if (!triggerWidth && containerRef.current) {
      const width = containerRef.current.offsetWidth;
      if (width) setTriggerWidth(width);
    }

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div ref={containerRef} className={cn("relative rounded text-left", fullWidth && "w-full")}>
      <Popover open={showSearchResult} onOpenChange={setShowSearchResult} modal={false}>
        <PopoverTrigger asChild>
          <div
            ref={triggerRef}
            className="w-full"
            onClick={(e) => {
              // If popover is already open and click is on input, prevent toggle
              if (
                showSearchResult &&
                (inputRef.current === e.target ||
                  inputRef.current?.contains(e.target as HTMLElement))
              ) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            {renderActivator()}
          </div>
        </PopoverTrigger>
        <PopoverContent
          ref={popoverContentRef}
          className="p-0 bg-white border border-gray-200 shadow-lg transition-none"
          style={{
            width: triggerWidth ? `${triggerWidth}px` : "var(--radix-popover-trigger-width)",
          }}
          align="start"
          side="bottom"
          sideOffset={4}
          onOpenAutoFocus={handleOpenAutoFocus}
          onClick={(e) => e.stopPropagation()}
          onPointerDownOutside={handlePointerDownOutside}
          onFocusOutside={handleFocusOutside}
          onInteractOutside={handleInteractOutside}
        >
          <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <div className="absolute right-2 top-2 inline-flex items-center justify-center z-10">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setShowSearchResult(false);
                  setIsExpanded(false);
                  setRawQuery("");
                  if (inputRef.current) inputRef.current.value = "";
                }}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              >
                <XCircleIcon className="w-5 h-5 text-gray-400 hover:text-blue-600" />
              </button>
            </div>
            {renderTypeActions()}
            {renderSearchResult()}
            {totalElements === 0 && !pressSearch && (
              <div className="flex flex-wrap items-center bg-gray-50 px-4 py-2.5 text-xs text-gray-700">
                {t("Press enter to search")}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
